'use client';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, X, FolderOpen, Sparkles, FileText, Image, Wand2, ChevronRight, Loader2, FileCode } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import Icon from '@/components/ui/AppIcon';
import { VISUAL_STUDIO_ENABLED } from '@/lib/visualStudioConfig';

interface SearchResult {
  id: string;
  title: string;
  subtitle: string;
  href: string;
  category: string;
  icon: React.ElementType;
  iconColor: string;
}

export default function GlobalSearch() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const router = useRouter();
  const supabase = createClient();

  const staticResults: SearchResult[] = useMemo(
    () => [
      {
        id: 's1',
        title: 'Dream Builder™',
        subtitle: 'Create a new dream project',
        href: '/dream-workspace',
        category: 'Tools',
        icon: Wand2,
        iconColor: 'text-primary',
      },
      {
        id: 's2',
        title: 'Business Builder',
        subtitle: 'Build your business plan',
        href: '/business-builder',
        category: 'Tools',
        icon: Sparkles,
        iconColor: 'text-yellow-400',
      },
      ...(VISUAL_STUDIO_ENABLED
        ? [
            {
              id: 's3',
              title: 'Image Studio',
              subtitle: 'Generate AI images',
              href: '/image-studio',
              category: 'Tools',
              icon: Image,
              iconColor: 'text-blue-400',
            },
          ]
        : []),
      {
        id: 's3b',
        title: t?.nav?.svgStudio || 'SVG Studio',
        subtitle: 'Create vector SVG artwork',
        href: '/svg-studio',
        category: 'Tools',
        icon: FileCode,
        iconColor: 'text-yellow-400',
      },
      {
        id: 's4',
        title: 'Knowledge Vault™',
        subtitle: 'Your business knowledge base',
        href: '/business-knowledge-vault',
        category: 'Tools',
        icon: FileText,
        iconColor: 'text-mint',
      },
      {
        id: 's5',
        title: 'My Projects',
        subtitle: 'View all your projects',
        href: '/my-projects',
        category: 'Library',
        icon: FolderOpen,
        iconColor: 'text-orange-400',
      },
    ],
    [t]
  );

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [projectResults, setProjectResults] = useState<SearchResult[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchProjects = useCallback(async (q: string) => {
    if (!user || !q.trim()) {
      setProjectResults([]);
      return;
    }
    try {
      const { data } = await supabase
        .from('dream_projects')
        .select('id, title, dream, created_at')
        .eq('user_id', user.id)
        .or(`title.ilike.%${q}%,dream.ilike.%${q}%`)
        .limit(5);

      if (data) {
        setProjectResults(data.map(p => {
          const label = p.title || p.dream || 'Untitled Project';
          return {
            id: p.id,
            title: label.length > 50 ? `${label.slice(0, 50)}...` : label,
            subtitle: `Dream Project · ${new Date(p.created_at).toLocaleDateString()}`,
            href: `/dream-workspace?projectId=${p.id}`,
            category: 'Projects',
            icon: FolderOpen,
            iconColor: 'text-orange-400',
          };
        }));
      }
    } catch {
      // silently fail
    }
  }, [user, supabase]);

  useEffect(() => {
    if (!query.trim()) {
      setResults(staticResults);
      setProjectResults([]);
      setLoading(false);
      return;
    }

    const filtered = staticResults.filter(
      (r) =>
        r.title.toLowerCase().includes(query.toLowerCase()) ||
        r.subtitle.toLowerCase().includes(query.toLowerCase())
    );
    setResults(filtered);

    setLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      await searchProjects(query);
      setLoading(false);
    }, 300);
  }, [query, searchProjects, staticResults]);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Keyboard shortcut
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSelect = (href: string) => {
    setOpen(false);
    setQuery('');
    router.push(href);
  };

  const allResults = [...projectResults, ...results];
  const grouped = allResults.reduce<Record<string, SearchResult[]>>((acc, r) => {
    if (!acc[r.category]) acc[r.category] = [];
    acc[r.category].push(r);
    return acc;
  }, {});

  return (
    <div className="relative" ref={panelRef}>
      {/* Search input */}
      <div className="relative hidden sm:block">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={t?.topbar?.searchPlaceholder || 'Search projects, tools... ⌘K'}
          className="input-dark search-input pl-9 pr-4 py-2 rounded-lg text-sm w-56 lg:w-72"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Results panel */}
      {open && (
        <div
          className="absolute top-full left-0 mt-2 w-80 lg:w-96 rounded-xl bg-card border border-border shadow-2xl overflow-hidden"
          style={{ zIndex: 9999, animation: 'searchSlideDown 0.15s ease-out' }}
        >
          <style>{`
            @keyframes searchSlideDown {
              from { opacity: 0; transform: translateY(-6px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          {loading && (
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border/50">
              <Loader2 size={12} className="text-primary animate-spin" />
              <span className="text-xs text-muted-foreground">Searching...</span>
            </div>
          )}

          {Object.keys(grouped).length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
              <Search size={24} className="text-muted-foreground/30 mb-2" />
              <p className="text-sm font-500 text-foreground mb-1">No results found</p>
              <p className="text-xs text-muted-foreground">Try a different search term</p>
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto py-1">
              {Object.entries(grouped).map(([category, items]) => (
                <div key={category}>
                  <div className="px-4 py-2 sticky top-0 bg-card/90 backdrop-blur-sm">
                    <p className="text-[10px] font-600 uppercase tracking-wider text-muted-foreground">{category}</p>
                  </div>
                  {items.map(result => {
                    const Icon = result.icon;
                    return (
                      <button
                        key={result.id}
                        onClick={() => handleSelect(result.href)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors text-left group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center shrink-0">
                          <Icon size={15} className={result.iconColor} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-500 text-foreground truncate group-hover:text-primary transition-colors">{result.title}</p>
                          <p className="text-xs text-muted-foreground truncate">{result.subtitle}</p>
                        </div>
                        <ChevronRight size={13} className="text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          <div className="px-4 py-2 border-t border-border bg-muted/10">
            <p className="text-[10px] text-muted-foreground">
              Press <kbd className="px-1 py-0.5 rounded bg-muted text-muted-foreground text-[10px]">↵</kbd> to select · <kbd className="px-1 py-0.5 rounded bg-muted text-muted-foreground text-[10px]">Esc</kbd> to close
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
